import React, { useState, useEffect, useContext, createContext } from "react";
import AuthService from "../services/auth.service";

const authContext = createContext();

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}


// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useAuth = () => {
  return useContext(authContext);
};

// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const ADMIN_ROLE = "admin";
  const AGENCY_ROLE = "agency";
  const SUBMIT_ROLE = "submitonly";
  const STANDARD_ROLE = "readonly";
  // #73 — Applied GA "Lite Admin": internal AGA staff with a trimmed console,
  // edit but no delete, no carrier/API-log/import access.
  const LITE_ROLE = "lite-admin";
  const AGENT_ROLE = "agent";
  // Internal/admin-console roles. Self-service "agent" accounts are NOT staff.
  const STAFF_ROLES = [ADMIN_ROLE, AGENCY_ROLE, SUBMIT_ROLE, STANDARD_ROLE, LITE_ROLE];

  // #50 — a principal (role "agency") is often ALSO an agent (their User carries
  // an `agent` reference). Such an account can act in two capacities: as the
  // agency's principal (the admin console) or as their own agent (the agent
  // dashboard + flows). `viewMode` is which capacity they're currently acting in;
  // it's a client-side intent only (the backend still authorizes by the real role
  // and sources the agent from user.agent), persisted so it survives navigation.
  const [viewMode, setViewModeState] = useState(() => {
    try {
      return localStorage.getItem("viewMode") || AGENCY_ROLE;
    } catch (e) {
      return AGENCY_ROLE;
    }
  });
  const setViewMode = (mode) => {
    try {
      localStorage.setItem("viewMode", mode);
    } catch (e) {
      // ignore storage failures; mode still applies for this session
    }
    setViewModeState(mode);
  };

  // Every principal can switch to their agent view. Principals are producing
  // agents (the GA/SGA/MGA/FMO override accounts), so they all get the toggle; a
  // principal not yet linked to an Agent record can browse the agent view, but
  // SUBMITTING a request needs that link (established by the #2 principal-account
  // load) since the flows are anchored on the Agent record.
  const canActAsAgent = (u) => u?.role === AGENCY_ROLE;
  // True when a principal is currently in agent mode (or the user is a plain
  // agent). Use this wherever the AGENT EXPERIENCE is gated, so a principal acting
  // as their agent is treated like an agent.
  const actingAsAgent = (u = user) =>
    u?.role === AGENT_ROLE || (canActAsAgent(u) && viewMode === AGENT_ROLE);

  useEffect(() => {
    const fetchData = async () => {
      const userInfo = await AuthService.getCurrentUser();
      if (userInfo) setUser(userInfo);
      setLoading(false);
    }
    // call the function
    fetchData()
  }, []);

  const isAdmin = (user) => {
    return ["admin", "agency"].includes(user?.role);
  }

  const isFullAdmin = (user) => {
    return user?.role === ADMIN_ROLE;
  }

  const isAgencyAdmin = (user) => {
    return user?.role === AGENCY_ROLE;
  }

  const isSubmitOnlyUser = (user) => {
    return user?.role === SUBMIT_ROLE;
  }

  const isStandardUser = (user) => {
    return user?.role === STANDARD_ROLE || user?.role === SUBMIT_ROLE;
  }

  // #73 — Applied GA Lite Admin.
  const isLiteAdmin = (user) => {
    return user?.role === LITE_ROLE;
  }

  // Whether this user may delete records at all. Deletes are locked to full
  // Applied GA admins app-wide (backend requireFullAdmin / super-admin
  // deleteMiddleware), so anyone else (Lite Admin, read-only, agency principal)
  // never gets a working delete control. Used to hide delete UI that would 403.
  const canDelete = (user) => {
    return user?.role === ADMIN_ROLE;
  }

  const canSubmit = (user) => {
    return user?.role !== STANDARD_ROLE;
  }

  // Self-service agent (not staff). Mutually exclusive with the staff roles.
  const isAgent = (user) => {
    return user?.role === AGENT_ROLE;
  }

  // Any internal/admin-console user.
  const isStaff = (user) => {
    return STAFF_ROLES.includes(user?.role);
  }

  // #50/#47 — the agent's profile is complete when no required field is missing.
  // The authoritative, role-aware list is computed by the server on
  // /api/auth/current (user.agentMissingFields). This drives the field-driven
  // onboarding gate + form, replacing the single onboardingCompletedAt flag.
  const agentMissingFields = (user) => user?.agentMissingFields || [];
  const agentInfoComplete = (user) => agentMissingFields(user).length === 0;
  // Back-compat alias for the onboarding gate: "onboarded" == profile complete.
  const isOnboarded = (user) => agentInfoComplete(user);

  // refetch always bypasses the week-long user cache and re-reads the live
  // session, so callers get fresh server state (e.g. an agent flipped to
  // existing after their first submitted request).
  const refetch = async () => {
      const userInfo = await AuthService.getCurrentUser(true);
      if (userInfo) setUser(userInfo);
  }

  const signout = async () => {
    await AuthService.logout();
    setUser(null);
    // Reset the view mode so the next login starts in the default (agency) view.
    setViewMode(AGENCY_ROLE);
  };

  // Return the user object and auth methods
  return {
    user,
    loading,
    refetch,
    signout,
    isAdmin,
    isFullAdmin,
    isAgencyAdmin,
    isSubmitOnlyUser,
    isStandardUser,
    isLiteAdmin,
    canDelete,
    isAgent,
    isStaff,
    isOnboarded,
    agentInfoComplete,
    agentMissingFields,
    canSubmit,
    // #50 — principal agent/agency view toggle.
    viewMode,
    setViewMode,
    canActAsAgent,
    actingAsAgent,
  };
}
